import { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import { motion, AnimatePresence } from "framer-motion";

/* ── Helper components outside parent to prevent focus-loss bug ── */

function Card({ title, icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
      <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3 flex items-center gap-2">
        <span>{icon}</span> {title}
      </h2>
      {children}
    </div>
  );
}

function SaveBtn({ loading, onClick, label = "Save Changes", color = "green" }) {
  const base = color === "green"
    ? "bg-green-600 hover:bg-green-700"
    : "bg-blue-600 hover:bg-blue-700";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`px-6 py-2.5 ${base} text-white rounded-xl text-sm font-semibold disabled:opacity-60 transition`}
    >
      {loading ? "Saving…" : label}
    </button>
  );
}

function Msg({ text }) {
  if (!text) return null;
  const ok = text.startsWith("✅");
  return (
    <p className={`text-sm font-medium ${ok ? "text-green-600" : "text-red-500"}`}>{text}</p>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════ */
export default function AdminOnlineConsultation() {

  /* ── Fee ── */
  const [fee,        setFee]        = useState("");
  const [feeError,   setFeeError]   = useState("");
  const [feeMsg,     setFeeMsg]     = useState("");
  const [feeLoading, setFeeLoading] = useState(false);

  /* ── Disclaimers (one per request type) ── */
  const [discOnline,      setDiscOnline]      = useState("");
  const [discAppointment, setDiscAppointment] = useState("");
  const [discEnquiry,     setDiscEnquiry]     = useState("");
  const [disclaimerMsg,   setDisclaimerMsg]   = useState("");
  const [disclaimerLoading,setDisclaimerLoading] = useState(false);

  /* ── Razorpay keys ── */
  const [rzpKeyId,     setRzpKeyId]     = useState("");
  const [rzpKeySecret, setRzpKeySecret] = useState("");
  const [showSecret,   setShowSecret]   = useState(false);
  const [rzpMsg,       setRzpMsg]       = useState("");
  const [rzpLoading,   setRzpLoading]   = useState(false);

  /* ── Doctors ── */
  const [doctors,         setDoctors]         = useState([]);
  const [doctorsLoading,  setDoctorsLoading]  = useState(true);
  const [togglingId,      setTogglingId]      = useState(null);
  const [doctorsMsg,      setDoctorsMsg]      = useState("");

  /* ── Stats ── */
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, revenue: 0 });

  /* ── Load everything on mount ── */
  useEffect(() => {
    // Fee
    api.get("/settings/consultation_fee")
      .then(r => { if (r.data != null) setFee(String(r.data)); })
      .catch(() => {});

    // Disclaimers per type
    api.get("/settings/disclaimer_online")
      .then(r => { if (r.data) setDiscOnline(r.data); }).catch(() => {});
    api.get("/settings/disclaimer_appointment")
      .then(r => { if (r.data) setDiscAppointment(r.data); }).catch(() => {});
    api.get("/settings/disclaimer_enquiry")
      .then(r => { if (r.data) setDiscEnquiry(r.data); }).catch(() => {});
    // Legacy: pre-fill online disclaimer from old key if new one is empty
    api.get("/settings/online_consultation_disclaimer")
      .then(r => { if (r.data) setDiscOnline(prev => prev || r.data); }).catch(() => {});

    // Razorpay key id (we don't store secret on frontend for display)
    api.get("/settings/razorpay_key_id")
      .then(r => { if (r.data) setRzpKeyId(r.data); })
      .catch(() => {});

    // Doctors list (full, including inactive for online)
    api.get("/doctor/list-all")
      .then(r => setDoctors(r.data || []))
      .catch(() => {})
      .finally(() => setDoctorsLoading(false));

    // Requests stats
    api.get("/requests")
      .then(r => {
        const all = (r.data || []).filter(x => x.type === "Online");
        setStats({
          total:   all.length,
          paid:    all.filter(x => x.paymentStatus === "paid").length,
          pending: all.filter(x => x.status === "Pending").length,
          revenue: all.filter(x => x.paymentStatus === "paid").reduce((s, x) => s + (x.fees || 0), 0),
        });
      })
      .catch(() => {});
  }, []);

  /* ── Save fee ── */
  const saveFee = async () => {
    setFeeError(""); setFeeMsg("");
    const n = Number(fee);
    if (!fee || isNaN(n) || n <= 0) {
      setFeeError("Enter a valid fee amount in ₹");
      return;
    }
    setFeeLoading(true);
    try {
      await api.post("/settings", { key: "consultation_fee", value: n });
      setFeeMsg("✅ Fee updated successfully!");
    } catch {
      setFeeMsg("❌ Failed to save fee");
    } finally {
      setFeeLoading(false);
    }
  };

  /* ── Save all three disclaimers ── */
  const saveDisclaimers = async () => {
    setDisclaimerMsg("");
    setDisclaimerLoading(true);
    try {
      await Promise.all([
        api.post("/settings", { key: "disclaimer_online",      value: discOnline }),
        api.post("/settings", { key: "disclaimer_appointment",  value: discAppointment }),
        api.post("/settings", { key: "disclaimer_enquiry",      value: discEnquiry }),
        // Keep legacy key in sync with online disclaimer for backwards compat
        api.post("/settings", { key: "online_consultation_disclaimer", value: discOnline }),
      ]);
      setDisclaimerMsg("✅ All disclaimers saved!");
    } catch {
      setDisclaimerMsg("❌ Failed to save");
    } finally {
      setDisclaimerLoading(false);
    }
  };

  /* ── Save Razorpay keys ── */
  const saveRzpKeys = async () => {
    setRzpMsg("");
    if (!rzpKeyId.trim()) { setRzpMsg("❌ Key ID is required"); return; }
    setRzpLoading(true);
    try {
      // Store key_id in settings (displayable)
      await api.post("/settings", { key: "razorpay_key_id", value: rzpKeyId.trim() });
      // Store key_secret in settings (used by backend via /settings/:key route)
      if (rzpKeySecret.trim()) {
        await api.post("/settings", { key: "razorpay_key_secret", value: rzpKeySecret.trim() });
      }
      setRzpMsg("✅ Keys saved! Restart your backend server for changes to take effect.");
    } catch {
      setRzpMsg("❌ Failed to save keys");
    } finally {
      setRzpLoading(false);
    }
  };

  /* ── Toggle doctor availability for online ── */
  const toggleDoctor = async (doctor) => {
    setTogglingId(doctor._id);
    setDoctorsMsg("");
    try {
      const newVal = !doctor.availableOnline;
      await api.patch(`/doctor/online-availability/${doctor._id}`, { availableOnline: newVal });
      setDoctors(prev =>
        prev.map(d => d._id === doctor._id ? { ...d, availableOnline: newVal } : d)
      );
      setDoctorsMsg(`✅ Dr. ${doctor.name} is now ${newVal ? "available" : "unavailable"} for online consultations`);
      setTimeout(() => setDoctorsMsg(""), 3000);
    } catch {
      setDoctorsMsg("❌ Failed to update doctor availability");
    } finally {
      setTogglingId(null);
    }
  };

  const onlineDoctors = useMemo(() => doctors.filter(d => d.availableOnline), [doctors]);

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">💻 Online Consultation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage fees, availability, disclaimer and Razorpay settings
          </p>
        </div>
        <a href="/admin/requests"
          className="text-sm text-blue-600 hover:underline font-medium">
          View all requests →
        </a>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Bookings",  value: stats.total,           color: "bg-purple-50 text-purple-700"  },
          { label: "Paid",            value: stats.paid,            color: "bg-green-50 text-green-700"    },
          { label: "Pending",         value: stats.pending,         color: "bg-yellow-50 text-yellow-700"  },
          { label: "Revenue Earned",  value: `₹${stats.revenue}`,   color: "bg-blue-50 text-blue-700"      },
        ].map(s => (
          <div key={s.label} className={`${s.color} rounded-2xl p-4 text-center`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Consultation Fee ── */}
      <Card title="Consultation Fee" icon="💰">
        <p className="text-xs text-gray-400 -mt-2">
          This amount is charged to patients via Razorpay when booking an online consultation.
        </p>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">₹</span>
            <input
              type="number"
              min="0"
              placeholder="e.g. 500"
              value={fee}
              onChange={e => { setFee(e.target.value); setFeeError(""); setFeeMsg(""); }}
              className={`w-full border rounded-xl pl-8 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
                feeError ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
              }`}
            />
          </div>
          {fee && !feeError && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 text-sm font-bold text-green-700">
              ₹{fee} / session
            </div>
          )}
        </div>
        {feeError && <p className="text-red-500 text-xs">{feeError}</p>}
        <Msg text={feeMsg}/>
        <SaveBtn loading={feeLoading} onClick={saveFee} label="Update Fee"/>
      </Card>

      {/* ── Doctor Availability ── */}
      <Card title="Doctor Availability for Online" icon="🩺">
        <p className="text-xs text-gray-400 -mt-2">
          Toggle which doctors appear in the patient booking dropdown for online consultations.
          {onlineDoctors.length > 0 && (
            <span className="ml-1 font-semibold text-green-600">
              {onlineDoctors.length} doctor{onlineDoctors.length > 1 ? "s" : ""} currently available online.
            </span>
          )}
        </p>

        <AnimatePresence>
          {doctorsMsg && (
            <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0 }}>
              <Msg text={doctorsMsg}/>
            </motion.div>
          )}
        </AnimatePresence>

        {doctorsLoading ? (
          <p className="text-gray-400 text-sm py-4 text-center">Loading doctors…</p>
        ) : doctors.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 text-sm">No doctors found.</p>
            <a href="/admin/doctors" className="text-blue-500 text-xs hover:underline mt-1 inline-block">
              Add doctors →
            </a>
          </div>
        ) : (
          <div className="space-y-2">
            {doctors.map(doctor => (
              <motion.div
                key={doctor._id}
                layout
                className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                  doctor.availableOnline
                    ? "bg-green-50 border-green-200"
                    : "bg-gray-50 border-gray-100"
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                    doctor.availableOnline ? "bg-green-500" : "bg-gray-300"
                  }`}>
                    {doctor.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Dr. {doctor.name}</p>
                    <p className="text-xs text-gray-400">
                      {doctor.qualification || "—"}
                      {doctor.clinicName ? ` · ${doctor.clinicName}` : ""}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Status pill */}
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    doctor.availableOnline
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-200 text-gray-500"
                  }`}>
                    {doctor.availableOnline ? "✓ Online" : "Offline"}
                  </span>

                  {/* Toggle switch */}
                  <button
                    onClick={() => toggleDoctor(doctor)}
                    disabled={togglingId === doctor._id}
                    className="focus:outline-none disabled:opacity-50"
                    title={doctor.availableOnline ? "Remove from online" : "Make available online"}
                  >
                    <div className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                      doctor.availableOnline ? "bg-green-500" : "bg-gray-300"
                    }`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        doctor.availableOnline ? "translate-x-6" : "translate-x-0.5"
                      }`}/>
                    </div>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {onlineDoctors.length === 0 && !doctorsLoading && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-700 flex items-center gap-2">
            ⚠️ No doctors are currently available for online consultations. Patients won't be able to select a doctor.
          </div>
        )}
      </Card>

      {/* ── Disclaimers per type ── */}
      <Card title="Patient Disclaimers" icon="📋">
        <p className="text-xs text-gray-400 -mt-2">
          Set a different disclaimer for each request type. Patients must read and accept before submitting.
        </p>

        {[
          { label: "🖥️ Online Consultation", key: "online",      value: discOnline,      set: setDiscOnline,
            hint: "Shown before payment. Include refund policy, video requirements, etc." },
          { label: "📅 In-Person Appointment", key: "appointment", value: discAppointment, set: setDiscAppointment,
            hint: "Shown before submitting an in-person appointment request." },
          { label: "💬 Enquiry", key: "enquiry",   value: discEnquiry,     set: setDiscEnquiry,
            hint: "Shown before submitting a general enquiry." },
        ].map(({ label, key, value, set, hint }) => (
          <div key={key} className="border border-gray-100 rounded-2xl p-4 space-y-3 bg-gray-50/50">
            <p className="text-sm font-semibold text-gray-700">{label}</p>
            <p className="text-xs text-gray-400 -mt-1">{hint}</p>
            <textarea
              rows={5}
              placeholder={`Enter the disclaimer for ${label}…\n\nExample:\n• This consultation is for informational purposes only\n• Fee is non-refundable once confirmed`}
              value={value}
              onChange={e => { set(e.target.value); setDisclaimerMsg(""); }}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition resize-y bg-white"
            />
            {value && (
              <div>
                <p className="text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">Preview</p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 max-h-36 overflow-y-auto">
                  <p className="text-xs text-amber-900 whitespace-pre-wrap leading-relaxed">{value}</p>
                </div>
              </div>
            )}
          </div>
        ))}

        <Msg text={disclaimerMsg}/>
        <SaveBtn loading={disclaimerLoading} onClick={saveDisclaimers} label="Save All Disclaimers"/>
      </Card>

      {/* ── Razorpay Keys ── */}
      <Card title="Razorpay Integration" icon="💳">
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 -mt-2">
          <p className="font-semibold mb-1">⚠️ Important</p>
          <p className="text-xs leading-relaxed">
            These keys are also required in your backend <code className="bg-blue-100 px-1 rounded">.env</code> file
            as <code className="bg-blue-100 px-1 rounded">RAZORPAY_KEY_ID</code> and{" "}
            <code className="bg-blue-100 px-1 rounded">RAZORPAY_KEY_SECRET</code>.
            Saving here stores them for reference. After updating, restart your backend server.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key ID <span className="text-xs text-gray-400">(starts with rzp_test_ or rzp_live_)</span>
            </label>
            <input
              type="text"
              placeholder="rzp_test_xxxxxxxxxxxx"
              value={rzpKeyId}
              onChange={e => { setRzpKeyId(e.target.value); setRzpMsg(""); }}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-400 transition"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key Secret <span className="text-xs text-gray-400">(optional — stored securely)</span>
            </label>
            <div className="relative">
              <input
                type={showSecret ? "text" : "password"}
                placeholder="Enter key secret to update"
                value={rzpKeySecret}
                onChange={e => { setRzpKeySecret(e.target.value); setRzpMsg(""); }}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 pr-12 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-400 transition"
              />
              <button
                type="button"
                onClick={() => setShowSecret(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
                tabIndex={-1}
              >
                {showSecret ? "🙈" : "👁️"}
              </button>
            </div>
          </div>
        </div>

        {/* Test mode note */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-500">
          <p className="font-semibold text-gray-700 mb-1">🧪 Testing without real payment?</p>
          <p>Use Razorpay Test Mode keys from your{" "}
            <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer"
              className="text-blue-500 hover:underline">
              Razorpay Dashboard
            </a>.
            Test card: <code className="bg-gray-100 px-1 rounded">4111 1111 1111 1111</code>, any future expiry, any CVV.
          </p>
        </div>

        <Msg text={rzpMsg}/>
        <SaveBtn loading={rzpLoading} onClick={saveRzpKeys} label="Save Razorpay Keys" color="blue"/>
      </Card>

      {/* ── How it works ── */}
      <Card title="How the Flow Works" icon="🔄">
        <div className="space-y-3">
          {[
            { step:"1", label:"Patient fills form",        desc:"Name, phone, doctor, date, time, timezone, health concern" },
            { step:"2", label:"Disclaimer shown",          desc:"Patient reads and accepts your disclaimer text above"       },
            { step:"3", label:"Razorpay checkout opens",   desc:`Patient pays ₹${fee || "?"} via card/UPI/netbanking`       },
            { step:"4", label:"Signature verified",        desc:"Backend verifies the payment is genuine using HMAC-SHA256"  },
            { step:"5", label:"Request auto-submitted",    desc:"Only after verification — no double-dipping possible"       },
            { step:"6", label:"Admin sees it here",        desc:"In Dashboard under 💻 Online tab with 💳 Paid badge"        },
          ].map(s => (
            <div key={s.step} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                {s.step}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">{s.label}</p>
                <p className="text-xs text-gray-400">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

    </div>
  );
}
